/**
 * Service Keycloak pour la plateforme Anamnesis
 * Gère l'authentification SSO via Keycloak
 */

import type { ApiResponse } from '@/types/api';
import type { LoginResponse, User } from '@/types/auth';
import { apiClient } from './api';

/**
 * Configuration Keycloak retournée par le backend
 */
export interface KeycloakConfig {
  enabled: boolean;
  server_url?: string;
  realm?: string;
  client_id?: string;
}

/**
 * Réponse de l'URL d'autorisation Keycloak
 */
export interface KeycloakAuthUrlResponse {
  auth_url: string;
  state: string;
}

/**
 * Requête pour échanger un code contre un token
 */
export interface KeycloakTokenRequest {
  code: string;
  redirect_uri: string;
  state?: string;
}

/**
 * Réponse après échange du code d'autorisation
 */
export interface KeycloakTokenResponse {
  access_token: string;
  token_type: string;
  user: User;
  refresh_token?: string;
  expires_in?: number;
}

/**
 * Réponse de l'URL de déconnexion Keycloak
 */
export interface KeycloakLogoutUrlResponse {
  logout_url: string;
}

/**
 * Classe de service Keycloak
 */
class KeycloakService {
  private config: KeycloakConfig | null = null;
  private configPromise: Promise<KeycloakConfig> | null = null;

  /**
   * Récupère la configuration Keycloak depuis le backend
   * Le résultat est mis en cache
   */
  async getConfig(): Promise<KeycloakConfig> {
    if (this.config) {
      return this.config;
    }

    if (this.configPromise) {
      return this.configPromise;
    }

    this.configPromise = (async () => {
      try {
        // Skip the network call unless Keycloak is explicitly enabled at build time
        if (import.meta.env.VITE_KEYCLOAK_ENABLED !== 'true') {
          this.config = { enabled: false };
          return this.config;
        }
        const response: ApiResponse<KeycloakConfig> = await apiClient.get('/auth/keycloak/config');
        this.config = response.data;
        return this.config;
      } catch (error) {
        console.error('Failed to fetch Keycloak config:', error);
        this.config = { enabled: false };
        return this.config;
      } finally {
        this.configPromise = null;
      }
    })();

    return this.configPromise;
  }

  /**
   * Vérifie si Keycloak est activé
   */
  async isEnabled(): Promise<boolean> {
    const config = await this.getConfig();
    return config.enabled;
  }

  /**
   * Vérifie si Keycloak est activé (version synchrone basée sur le cache)
   */
  isEnabledSync(): boolean {
    return this.config?.enabled ?? false;
  }

  /**
   * Génère l'URL de callback pour Keycloak
   */
  getCallbackUrl(): string {
    const baseUrl = window.location.origin;
    return `${baseUrl}/sso/callback`;
  }

  /**
   * Récupère l'URL d'autorisation Keycloak pour initier le flux OAuth
   */
  async getAuthUrl(): Promise<KeycloakAuthUrlResponse> {
    const redirectUri = this.getCallbackUrl();

    const response: ApiResponse<KeycloakAuthUrlResponse> = await apiClient.get(
      `/auth/keycloak/auth-url?redirect_uri=${encodeURIComponent(redirectUri)}`
    );

    // Stocker le state pour la vérification CSRF
    sessionStorage.setItem('keycloak_state', response.data.state);

    return response.data;
  }

  /**
   * Redirige vers la page de connexion Keycloak
   */
  async login(): Promise<void> {
    const { auth_url } = await this.getAuthUrl();
    window.location.href = auth_url;
  }

  /**
   * Échange un code d'autorisation contre des tokens
   */
  async exchangeCode(code: string, state?: string): Promise<KeycloakTokenResponse> {
    // Vérifier le state pour la protection CSRF
    const storedState = sessionStorage.getItem('keycloak_state');
    if (state && storedState && state !== storedState) {
      throw new Error('Invalid state parameter - possible CSRF attack');
    }

    // Nettoyer le state après utilisation
    sessionStorage.removeItem('keycloak_state');

    const redirectUri = this.getCallbackUrl();

    const tokenRequest: KeycloakTokenRequest = {
      code,
      redirect_uri: redirectUri,
      state,
    };

    const response: ApiResponse<KeycloakTokenResponse> = await apiClient.post(
      '/auth/keycloak/token',
      tokenRequest
    );

    return response.data;
  }

  /**
   * Traite le callback OAuth de Keycloak
   * Extrait le code et le state de l'URL et les échange contre des tokens
   */
  async handleCallback(): Promise<LoginResponse> {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (error) {
      throw new Error(`Keycloak error: ${error} - ${errorDescription || 'Unknown error'}`);
    }

    if (!code) {
      throw new Error('No authorization code received from Keycloak');
    }

    const tokenResponse = await this.exchangeCode(code, state || undefined);

    // Convertir la réponse au format LoginResponse standard
    return {
      access_token: tokenResponse.access_token,
      token_type: 'bearer' as const,
      user: tokenResponse.user,
    };
  }

  /**
   * Récupère l'URL de déconnexion Keycloak
   */
  async getLogoutUrl(): Promise<string> {
    const redirectUri = window.location.origin;

    const response: ApiResponse<KeycloakLogoutUrlResponse> = await apiClient.get(
      `/auth/keycloak/logout-url?redirect_uri=${encodeURIComponent(redirectUri)}`
    );

    return response.data.logout_url;
  }

  /**
   * Déconnexion via Keycloak
   */
  async logout(): Promise<void> {
    try {
      const logoutUrl = await this.getLogoutUrl();
      window.location.href = logoutUrl;
    } catch (error) {
      console.error('Failed to get Keycloak logout URL:', error);
      // En cas d'erreur, effectuer une déconnexion locale simple
      window.location.href = '/';
    }
  }

  /**
   * Rafraîchit la configuration Keycloak
   */
  async refreshConfig(): Promise<KeycloakConfig> {
    this.config = null;
    this.configPromise = null;
    return this.getConfig();
  }

  /**
   * Vérifie si l'URL actuelle est le callback Keycloak
   */
  isCallbackUrl(): boolean {
    return window.location.pathname === '/sso/callback' ||
           window.location.pathname === '/auth/callback';
  }

  /**
   * Vérifie si le callback contient un code d'autorisation
   */
  hasAuthorizationCode(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('code');
  }

  /**
   * Vérifie si le callback contient une erreur
   */
  hasError(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('error');
  }

  /**
   * Récupère le message d'erreur du callback
   */
  getErrorMessage(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (!error) return null;

    return errorDescription || error;
  }
}

// Instance singleton
export const keycloakService = new KeycloakService();

// Export de la classe pour les tests
export { KeycloakService };
